import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Copy, Printer, QrCode, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { getVisitorPass, type VisitorPass } from "../../api/visitors";
import { STATUS_BADGE, STATUS_LABEL, errMsg, fmtClock, fmtDate } from "./visitorUi";

/**
 * The printable visitor pass: the QR, the short code under it, and the few
 * details a security desk reads off it.
 *
 * The QR arrives from the backend as an SVG string (rendered server-side from
 * the pass token, so no QR library ships to the browser) and is inlined with
 * `dangerouslySetInnerHTML`. That is safe here and nowhere else in this
 * module: the markup is generated by `utils/visitor_qr.py::qr_svg` from a
 * fixed template whose only variable is a path of integers — no user text ever
 * reaches it.
 */
export default function VisitorPassDialog({
  visitorId,
  onClose,
}: {
  visitorId: string | null;
  onClose: () => void;
}) {
  const [pass, setPass] = useState<VisitorPass | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visitorId) {
      setPass(null);
      return;
    }
    let alive = true;
    setLoading(true);
    getVisitorPass(visitorId)
      .then((p) => {
        if (alive) setPass(p);
      })
      .catch((e) => toast.error(errMsg(e, "Could not load the visitor pass")))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [visitorId]);

  const copyCode = async () => {
    if (!pass?.pass_code) return;
    try {
      await navigator.clipboard.writeText(pass.pass_code);
      toast.success("Pass code copied");
    } catch {
      // Clipboard is blocked outside a secure context / without permission.
      toast.error(`Copy failed — the code is ${pass.pass_code}`);
    }
  };

  const printPass = () => {
    if (!pass) return;
    const win = window.open("", "_blank", "width=420,height=640");
    if (!win) {
      toast.error("Allow pop-ups to print the pass");
      return;
    }
    // Written as a standalone document rather than printing the dialog: the
    // app's stylesheet would drag the whole page layout into the print.
    const esc = (s: string | null | undefined) =>
      String(s ?? "—").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] as string));
    win.document.write(`<!doctype html><html><head><meta charset="utf-8">
<title>Visitor Pass</title>
<style>
  body{font-family:system-ui,-apple-system,"Segoe UI",sans-serif;margin:0;padding:24px;color:#111827}
  .card{border:1px solid #e5e7eb;border-radius:12px;padding:20px;text-align:center;max-width:320px;margin:0 auto}
  h1{font-size:14px;letter-spacing:.08em;text-transform:uppercase;color:#6b7280;margin:0 0 12px}
  .name{font-size:20px;font-weight:600;margin:14px 0 2px}
  .meta{font-size:12px;color:#6b7280;margin:2px 0}
  .code{margin-top:14px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:18px;letter-spacing:.12em}
  .foot{margin-top:12px;font-size:10px;color:#9ca3af}
  svg{width:220px;height:220px}
</style></head><body><div class="card">
  <h1>Visitor Pass</h1>
  ${pass.qr_svg}
  <div class="code">${esc(pass.pass_code)}</div>
  <div class="name">${esc(pass.visitor_name)}</div>
  <div class="meta">Host: ${esc(pass.host_name)}</div>
  <div class="meta">${esc(fmtDate(pass.visit_date))} · ${esc(fmtClock(pass.expected_time))}</div>
  <div class="meta">${esc(pass.purpose)}</div>
  <div class="foot">Show this pass at reception for entry and exit.</div>
</div><script>window.onload=function(){window.print()}</script></body></html>`);
    win.document.close();
  };

  return (
    <Dialog open={!!visitorId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode size={18} /> Visitor Pass
          </DialogTitle>
          <DialogDescription>
            Send or print this for the visitor to show at reception.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : !pass ? (
          <div className="py-12 text-center text-sm text-gray-500">
            Pass not available.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 p-5 text-center">
              <div
                className={`mx-auto w-[220px] h-[220px] flex items-center justify-center ${
                  pass.usable ? "" : "opacity-30"
                }`}
                // See the component docstring: server-generated, no user text.
                dangerouslySetInnerHTML={{ __html: pass.qr_svg }}
              />
              <div className="mt-3 font-mono text-lg tracking-[0.12em] text-gray-900">
                {pass.pass_code}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Can't scan? Reception can enter this code.
              </div>
            </div>

            {!pass.usable && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                <span>
                  This pass is {STATUS_LABEL[pass.status].toLowerCase()} and will be
                  refused at the desk. Issue a new invitation instead of sending it.
                </span>
              </div>
            )}

            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-gray-900">{pass.visitor_name}</span>
                <Badge className={`rounded-md ${STATUS_BADGE[pass.status]}`}>
                  {STATUS_LABEL[pass.status]}
                </Badge>
              </div>
              <div className="text-gray-600">Host: {pass.host_name || "—"}</div>
              <div className="text-gray-600">
                {fmtDate(pass.visit_date)} · {fmtClock(pass.expected_time)}
              </div>
              {pass.purpose && <div className="text-gray-600">{pass.purpose}</div>}
            </div>
          </div>
        )}

        <DialogFooter>
          <button className="gt-btn is-ghost" onClick={copyCode} disabled={!pass}>
            <Copy size={14} /> Copy code
          </button>
          <button className="gt-btn is-primary" onClick={printPass} disabled={!pass}>
            <Printer size={14} /> Print pass
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
